import { isColorDark } from "utils/colorContrast";
// REACT IMPORTS
import { useMemo } from "react";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// COMPONENT IMPORTS
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import {
  fetchScheduleInterval,
  getLeadData,
  getCustomPlan,
  getDashboardData,
  getHolidays,
  getNewPlan,
  getPaymentDates,
  getPaymentScheduleData,
  getPortalSettings,
  getStateDisclaimer,
  getUpdateScheduleData,
  getUpdateSubscriptionData,
} from "store/services/dashboardServices";
import { DashboardTypes } from "types/dashboard";
import { AccountLeadData } from "types/authTypes";
import { resolvePortalBaseUrl } from "utils/portalVanityUrl";

interface InitialState {
  dashboardData: any;
  paymentPlanData: any;
  accountLeadData: AccountLeadData | any;
  getPaymentNewPlan: any;
  getPaymentCustomPlan: any;
  holidayList: any;
  portalSettings: any;
  portalSettingsStatus: "idle" | "loading" | "succeeded" | "failed";
  portalSettingsClusterId: string | null;
  portalSettingsRequestId: string | null;
  stateDisclaimerByKey: Record<string, StateDisclaimerEntry>;
  brandColor: string;
  isDark: any;
  accentColor: string;
  isDashPreview: boolean;
}

export type StateDisclaimerStatus = "idle" | "loading" | "succeeded" | "failed";

export interface StateDisclaimerParams {
  account_id?: string | number;
  cluster_id?: string;
}

export interface StateDisclaimerData {
  initial_disclosure?: string;
  final_disclosure?: string;
  authorization_text?: string;
  tnc_text?: string;
}

export interface StateDisclaimerEntry {
  data: StateDisclaimerData;
  status: StateDisclaimerStatus;
  requestId: string | null;
}

const EMPTY_STATE_DISCLAIMER_ENTRY: StateDisclaimerEntry = {
  data: {},
  status: "idle",
  requestId: null,
};

export const getStateDisclaimerRequestKey = ({
  account_id,
  cluster_id,
}: StateDisclaimerParams): string =>
  JSON.stringify([String(cluster_id ?? ""), String(account_id ?? "")]);

const initialState: InitialState = {
  dashboardData: {},
  paymentPlanData: {},
  accountLeadData: {},
  getPaymentNewPlan: {},
  getPaymentCustomPlan: {},
  holidayList: [],
  portalSettings: {},
  portalSettingsStatus: "idle",
  portalSettingsClusterId: null,
  portalSettingsRequestId: null,
  stateDisclaimerByKey: {},
  brandColor: "#f3f4f8",
  isDark: false,
  accentColor: "#1678f2",
  isDashPreview: false,
};

export const dashboardDataGet = createAsyncThunk(
  "dashboard-data/get",
  async (params: any) => {
    return await getDashboardData(params);
  }
);

export const paymentPlanData = createAsyncThunk(
  "payment-plan-data/get",
  async (params: any) => {
    return await getPaymentScheduleData(params);
  }
);

export const paymentUpdateSubscriptionData = createAsyncThunk(
  "payment-update-schedule-data/get",
  async (params: any) => {
    return await getUpdateSubscriptionData(params);
  }
);

export const paymentUpdateScheduleData = createAsyncThunk(
  "payment-update-schedule-data/get",
  async (params: any) => {
    return await getUpdateScheduleData(params);
  }
);

export const leadData = createAsyncThunk(
  "payment-lead-data/get",
  async (params: any) => {
    return await getLeadData(params);
  }
);

export const getPaymentPlanDates = createAsyncThunk(
  "payment-dates/get",
  async (payload: any) => {
    return await getPaymentDates(payload);
  }
);

export const getPaymentNewPlan = createAsyncThunk(
  "payment-new-plan/get",
  async (payload: any) => {
    return await getNewPlan(payload);
  }
);

export const getPaymentCustomPlan = createAsyncThunk(
  "payment-custom-plan/get",
  async (payload: any) => {
    return await getCustomPlan(payload);
  }
);

export const getHolidayList = createAsyncThunk(
  "payment-holiday-list/get",
  async (params: any) => {
    return await getHolidays(params);
  }
);

export const portalSettingsGet = createAsyncThunk(
  "portal-settings/get",
  async (params: any) => {
    return await getPortalSettings(params);
  }
);

export const getScheduleInterval = createAsyncThunk(
  "schedule-date/get",
  async (params: any) => {
    return await fetchScheduleInterval(params);
  }
);

export const stateDisclaimerGet = createAsyncThunk<
  any,
  StateDisclaimerParams,
  { state: RootState }
>(
  "state-disclaimer/get",
  async (params) => {
    return await getStateDisclaimer(params);
  },
  {
    condition: (params, { getState }) => {
      const requestKey = getStateDisclaimerRequestKey(params);
      const existing = getState().dashboard.stateDisclaimerByKey[requestKey];

      return (
        !existing ||
        (existing.status !== "loading" && existing.status !== "succeeded")
      );
    },
  }
);

/* ------------------------------------- DASHBOARD SLICE ------------------------------------- */

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardSlice: (state) => {
      for (const key in initialState) {
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof InitialState;
          // @ts-ignore
          state[typedKey] = initialState[typedKey];
        }
      }
    },
    setIsDashPreview: (state, action) => {
      state.isDashPreview = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      dashboardDataGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload?.body?.success) {
          state.dashboardData = action.payload?.body?.data;
        }
      }
    );
    builder.addCase(
      paymentUpdateScheduleData.fulfilled,
      (state, action: PayloadAction<any>) => {
        let clientScheduleCustomData =
          action.payload?.body?.data?.clientCustomerData
            ?.clientScheduleCustomData;

        state.paymentPlanData = {
          clientCustomerData: action.payload?.body?.data?.clientCustomerData,
          clinetScheduleCustomData: clientScheduleCustomData,
        };
      }
    );
    builder.addCase(
      paymentPlanData.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.paymentPlanData = action.payload?.body?.data;
      }
    );
    builder.addCase(
      getPaymentNewPlan.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.getPaymentNewPlan = action.payload?.body?.data;
      }
    );
    builder.addCase(leadData.fulfilled, (state, action: PayloadAction<any>) => {
      state.accountLeadData = action.payload?.body?.data;
    });
    builder.addCase(
      getHolidayList.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload.body.success) {
          state.holidayList = action.payload.body.data;
        }
      }
    );
    builder
      .addCase(portalSettingsGet.pending, (state, action) => {
        state.portalSettings = {};
        state.portalSettingsStatus = "loading";
        state.portalSettingsClusterId = String(
          action.meta.arg?.cluster_id || ""
        );
        state.portalSettingsRequestId = action.meta.requestId;
      })
      .addCase(portalSettingsGet.fulfilled, (state, action: any) => {
        if (state.portalSettingsRequestId !== action.meta.requestId) {
          return;
        }

        const settings =
          action?.payload?.body?.success && action?.payload?.body?.data
            ? action.payload.body.data
            : {};

        state.portalSettings = settings;
        state.portalSettingsStatus = "succeeded";
        state.portalSettingsClusterId = String(
          action.meta.arg?.cluster_id || ""
        );
        state.portalSettingsRequestId = null;

        if (Object.keys(settings).length > 0) {
          const isDark = isColorDark(settings.brand_color || "#f3f4f8");
          state.brandColor = settings.brand_color || "#f3f4f8";
          state.accentColor = settings.accent_color || "#1678f2";
          state.isDark = isDark;
        }
      })
      .addCase(portalSettingsGet.rejected, (state, action) => {
        if (state.portalSettingsRequestId !== action.meta.requestId) {
          return;
        }

        state.portalSettings = {};
        state.portalSettingsStatus = "failed";
        state.portalSettingsClusterId = String(
          action.meta.arg?.cluster_id || ""
        );
        state.portalSettingsRequestId = null;
      })
      .addCase(stateDisclaimerGet.pending, (state, action) => {
        const requestKey = getStateDisclaimerRequestKey(action.meta.arg);
        state.stateDisclaimerByKey[requestKey] = {
          data: state.stateDisclaimerByKey[requestKey]?.data || {},
          status: "loading",
          requestId: action.meta.requestId,
        };
      })
      .addCase(stateDisclaimerGet.fulfilled, (state, action) => {
        const requestKey = getStateDisclaimerRequestKey(action.meta.arg);
        const existing = state.stateDisclaimerByKey[requestKey];

        if (existing?.requestId !== action.meta.requestId) {
          return;
        }

        state.stateDisclaimerByKey[requestKey] = {
          data:
            action.payload?.body?.success && action.payload?.body?.data
              ? action.payload.body.data
              : {},
          status: "succeeded",
          requestId: null,
        };
      })
      .addCase(stateDisclaimerGet.rejected, (state, action) => {
        const requestKey = getStateDisclaimerRequestKey(action.meta.arg);
        const existing = state.stateDisclaimerByKey[requestKey];

        if (existing?.requestId !== action.meta.requestId) {
          return;
        }

        state.stateDisclaimerByKey[requestKey] = {
          data: existing?.data || {},
          status: "failed",
          requestId: null,
        };
      });
  },
});

export const { clearDashboardSlice, setIsDashPreview } = dashboardSlice.actions;
export default dashboardSlice.reducer;

const selectDashboardData = (state: RootState) =>
  state.dashboard.dashboardData as DashboardTypes;
export const useDashboardData = () => {
  const dashboardData = useAppSelector(selectDashboardData);
  return useMemo(() => dashboardData, [dashboardData]);
};

const selectLeadData = (state: RootState) => state.dashboard.accountLeadData;

export const useLeadData = () => {
  const accountLeadData = useAppSelector(selectLeadData);
  return useMemo(() => accountLeadData, [accountLeadData]);
};

const selectPaymentPlanData = (state: RootState) =>
  state.dashboard.paymentPlanData;
export const usePaymentPlanData = () => {
  const paymentPlanData = useAppSelector(selectPaymentPlanData);
  return useMemo(() => paymentPlanData, [paymentPlanData]);
};

const selectPaymentNewPlanData = (state: RootState) =>
  state.dashboard.getPaymentNewPlan;
export const usePaymentNewPlanData = () => {
  const getPaymentNewPlan = useAppSelector(selectPaymentNewPlanData);
  return useMemo(() => getPaymentNewPlan, [getPaymentNewPlan]);
};

const selectPortalSettingsData = (state: RootState) =>
  state.dashboard.portalSettings;
export const usePortalSettingsData = () => {
  const portalSettings = useAppSelector(selectPortalSettingsData);
  return useMemo(() => portalSettings, [portalSettings]);
};

export const selectStateDisclaimerEntry = (
  state: RootState,
  requestKey: string
): StateDisclaimerEntry =>
  state.dashboard.stateDisclaimerByKey[requestKey] ||
  EMPTY_STATE_DISCLAIMER_ENTRY;

export const usePortalVanityUrl = () => {
  const portalSettings = useAppSelector(selectPortalSettingsData);
  const domain =
    typeof window !== "undefined" && window.location.origin !== "null"
      ? window.location.origin
      : "";

  return useMemo(
    () => resolvePortalBaseUrl(portalSettings?.vanity_url, domain),
    [domain, portalSettings?.vanity_url]
  );
};
